<script lang="ts" setup>
import projectStore from '@/store/ProjectStore'
import FormElement from '@/components/FormElement.vue'
import formStore from '@/store/FormStore'
import userStore from '@/store/UserStore'
import { ValidatorType } from '@/helper/ValidateDirective'
import SectionHeadline from '@/components/SectionHeadline.vue'
import ComboBox from '@/components/ComboBox.vue'
import i18n from '@/helper/Translations'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import ButtonSet from '@/components/ButtonSet.vue'
import LegacyBuildingDimensions from '@/components/steps/planning/LegacyBuildingDimensions.vue'
import LivingSpaceData from '@/components/steps/planning/LivingSpaceData.vue'
import { BuildingHull } from '@/models/BuildingHull.ts'
import { BuildingStory } from '@/models/BuildingStory.ts'

const props = defineProps<{
  validator: ValidatorType
  areStoriesValid: boolean
}>()

const emit = defineEmits<{
  (e: 'updateLoad'): void
  (e: 'validateBuildingStories'): void
}>()

defineExpose({
  validator: props.validator
})

const useLivingSpaceInput = ref(!(formStore.data.buildingHull?.stories.length === 1 && formStore.data.buildingHull?.stories[0].width))
const buildingHull = ref<BuildingHull>(new BuildingHull())

onMounted(() => {
  if (!formStore.data.buildingHull) {
    formStore.data.buildingHull = new BuildingHull([new BuildingStory({ topStory: true, bottomStory: true })])
  }

  buildingHull.value = formStore.data.buildingHull
})

watch(useLivingSpaceInput, (newVal, oldVal) => {
  if (oldVal !== newVal) {
    formStore.data.buildingHull.stories = [new BuildingStory({ topStory: true, bottomStory: true })]
  }
})

watch(() => buildingHull.value, () => {
  emit('validateBuildingStories')

  nextTick(() => {
    if (props.areStoriesValid) {
      emit('updateLoad')
    }
  })
}, { deep: true })

const constructionYearOptions = computed(() => {
  return userStore.constructionYears.map(constructionYear => ({
    label: i18n.global.t(`step_heating_details.years_of_construction.${constructionYear}`),
    value: constructionYear
  }))
})
</script>

<template>
  <section class="form__section has--line section--building-data">
    <div class="form__section-inner">
      <SectionHeadline class="form__title" translation-key="step_heating_details.title_building_data" />

      <div class="form__subsection">
        <div class="form__description">
          {{ $t('step_heating_details.info_building_data') }}
        </div>

        <div id="construction-year" class="row form__row">
          <div class="col-md-6">
            <FormElement
              v-validate="projectStore.projectProperties.constructionYear.formValidationRule"
              :error="validator.yearOfConstruction?.error"
              :label="$t('step_heating_details.label_year_of_construction')"
              :required="projectStore.formPropertyIsRequired('constructionYear')"
            >
              <ComboBox
                id="year-of-construction"
                v-model="formStore.data.constructionYear"
                :options="constructionYearOptions"
                :placeholder="$t('step_heating_details.placeholder_year_of_construction')"
                @change="$emit('updateLoad')"
              />
            </FormElement>
          </div>
        </div>
      </div>

      <div class="form__subsection">
        <h3 class="form__subtitle">
          {{ $t('step_heating_details.label_building_dimensions') }}
        </h3>

        <ButtonSet
          key="button-set-building-area"
          v-model="useLivingSpaceInput"
          :buttons="[
            {
              caption: $t('step_heating_details.btn_building_data_livingspace'),
              value: true
            },
            {
              caption: $t('step_heating_details.btn_building_data_building_dimensions'),
              value: false
            }
          ]"
          :has-large-space="true"
          name="button-set-building-area"
        />

        <LivingSpaceData
          v-if="useLivingSpaceInput"
          :are-stories-valid="areStoriesValid"
          :building-stories="buildingHull.stories"
          :validator="validator"
        />

        <LegacyBuildingDimensions v-else :validator="validator" />
      </div>
    </div>
  </section>
</template>
